import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "../../constants";

const NewsletterSection = ({ isBangla = false }) => {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error(isBangla ? "নাম এবং ইমেইল দিন" : "Please enter name and email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(isBangla ? "সঠিক ইমেইল দিন" : "Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase()
      });
      
      if (response.data.already_subscribed) {
        toast.info(isBangla ? "আপনি ইতিমধ্যে সাবস্ক্রাইব করেছেন!" : "You're already subscribed!");
      } else if (response.data.resubscribed) {
        toast.success(isBangla ? "স্বাগতম! আবার সাবস্ক্রাইব করেছেন।" : "Welcome back! You've been resubscribed.");
        setSubscribed(true);
      } else {
        toast.success(isBangla ? "সফলভাবে সাবস্ক্রাইব করেছেন!" : "Successfully subscribed!");
        setSubscribed(true);
      }
      
      setFormData({ name: "", email: "" });
    } catch (error) {
      console.error("Newsletter subscribe error:", error);
      toast.error(isBangla ? "সাবস্ক্রাইব করতে সমস্যা হয়েছে" : "Failed to subscribe");
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <section className="bg-gradient-to-br from-green-500 to-emerald-600 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h2 className={`text-2xl md:text-3xl font-bold text-white mb-3 ${isBangla ? 'bangla-text' : ''}`}>
            {isBangla ? "ধন্যবাদ! সাবস্ক্রাইব করেছেন" : "Thank You for Subscribing!"}
          </h2>
          <p className={`text-white/90 ${isBangla ? 'bangla-text' : ''}`}>
            {isBangla 
              ? "আপনি এখন আমাদের নতুন ব্লগ এবং আপডেট পাবেন।" 
              : "You'll now receive our latest blogs and updates."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-[#ef4444] via-[#630000] to-[#630473] py-12 px-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-yellow-300" />
            <span className={`text-white text-sm font-medium ${isBangla ? 'bangla-text' : ''}`}>
              {isBangla ? "বিনামূল্যে সাবস্ক্রাইব করুন" : "Subscribe for Free"}
            </span>
          </div>
          
          <h2 className={`text-2xl md:text-3xl font-bold text-white mb-3 ${isBangla ? 'bangla-text' : ''}`}>
            {isBangla 
              ? "🎓 বিদেশে পড়াশোনার আপডেট পান!" 
              : "🎓 Stay Updated with Study Abroad News!"}
          </h2>
          
          <p className={`text-white/90 max-w-xl mx-auto ${isBangla ? 'bangla-text' : ''}`}>
            {isBangla 
              ? "সর্বশেষ ব্লগ, স্কলারশিপ আপডেট এবং স্টাডি গাইড সরাসরি আপনার ইমেইলে পান।" 
              : "Get the latest blogs, scholarship updates, and study guides directly in your inbox."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-3 bg-white/10 backdrop-blur-sm p-3 rounded-2xl">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={isBangla ? "আপনার নাম" : "Your Name"}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`h-12 bg-white border-0 rounded-xl text-gray-800 placeholder:text-gray-400 ${isBangla ? 'bangla-text' : ''}`}
                disabled={loading}
              />
            </div>
            <div className="flex-1">
              <Input
                type="email"
                placeholder={isBangla ? "আপনার ইমেইল" : "Your Email"}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 bg-white border-0 rounded-xl text-gray-800 placeholder:text-gray-400"
                disabled={loading}
              />
            </div>
            <Button 
              type="submit"
              disabled={loading}
              className={`h-12 px-6 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl ${isBangla ? 'bangla-text' : ''}`}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {isBangla ? "সাবস্ক্রাইব" : "Subscribe"}
                </>
              )}
            </Button>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 mt-6 text-white/80 text-sm">
            <span className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-300" />
              {isBangla ? "সর্বশেষ ব্লগ" : "Latest Blogs"}
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-300" />
              {isBangla ? "স্কলারশিপ আপডেট" : "Scholarship Alerts"}
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-300" />
              {isBangla ? "স্টাডি গাইড" : "Study Guides"}
            </span>
          </div>
        </form>
      </div>
    </section>
  );
};

export default NewsletterSection;
